import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import { earlyWarning } from "../earlyWarning/ShowEarlyWarning";
import { hzIncidents } from "../hazardIncidentsLayers/IncidentsMenuDropDown";
import floodIco from "../../assets/af.png";
import landSlideIco from "../../assets/al.png";
import windIco from "../../assets/aw.png";
import infoIcon from "../../assets/info-ico-color.png";
import safeIco from "../../assets/safe-ico-color.png";
import loadIco from "../../assets/loading-ico.gif";
import incidentIco from "../../assets/incident-icon-color.png";
import activeEWIco from "../../assets/ew-icon-color.png";
import closeIcon from "../../assets/close-icon-color.png";
import { updateMapCenter } from "../MainLayer";
import { useLayout } from "../../layout/LayoutContext";
import { useEWSContext } from "../../provider/EWSProvider";
import { useHzContext } from "../../provider/HzProvider";

type SummaryCompTypes = {
  windowState: Dispatch<React.SetStateAction<boolean>>;
  moreDetailWindow: Dispatch<React.SetStateAction<boolean>>;
  handleEarlyWarningData: (data: earlyWarning[]) => void;
  handleHzData: (data: hzIncidents[]) => void;
  ewSeverityWindow: Dispatch<React.SetStateAction<boolean>>;
  onFilteredWarnings: (data: earlyWarning[]) => void;
  onFilteredWarningsByType: (data: earlyWarning[]) => void;
  typeWindow: Dispatch<React.SetStateAction<boolean>>;
  onFilteredHzByType: (data: hzIncidents[]) => void;
  openModular: (value: string) => void;
  summeryType: (value: string) => void;
};

type SeverityCount = {
  Red: number;
  Yellow: number;
  Amber: number;
};

type WarningSummary = {
  [w_type: string]: SeverityCount;
};

type IncidentCountType = {
  count: number;
};

type IncidentSummary = {
  [w_type: string]: IncidentCountType;
};

const SummaryComp = (props: SummaryCompTypes) => {
  // Current value
  const [currentTime, setCurrentTime] = useState<number>(Date.now());
  const [warningSummary, setWarningSummary] = useState<WarningSummary>({});
  const [earlyWarningAll, setEarlyWarningAll] = useState<earlyWarning[]>([]);
  const [hzIncidents, setHZincidents] = useState<hzIncidents[]>([]);
  const [lastUpdate, setLastUpdate] = useState<number>(Date.now());
  const [incidents, setIncidents] = useState<hzIncidents[] | null>(null);
  // Early warnings
  const [earlyWarnings, setEarlyWarnings] = useState<earlyWarning[] | null>(
    null
  );
  const [incidentsSummary, setIncidentsSummary] = useState<IncidentSummary>({});
  const { layout } = useLayout();
  const { setClickedString, setEarlyWarningsFull } = useEWSContext();
  const { setClickedHzString } = useHzContext();

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const iconHzMap = new Map([
    ["ILSW", "assets/landslide_icon.png"],
    ["IFLW", "assets/flood_icon.png"],
    ["ITHS", "assets/lightning_icon.png"],
    ["HWN", "assets/cyclone_icon.png"],
    ["LIG", "assets/lightning_icon.png"],
    ["ILSS", "assets/landslide_icon.png"],
    ["ITSM", "assets/cyclone_icon.png"],
    ["HRW", "assets/iconm_heavyrain.png"],
    ["IFFIR", "assets/explosion_icon.png"],
    ["EXPL", "assets/explosion_icon.png"],
    ["IFIR", "assets/explosion_icon.png"],
    ["NUCL", "assets/nuclear_icon.png"],
    ["BIOL", "assets/virus_icon.png"],
    ["IEQA", "assets/earth_icon.png"],
  ]);
  const incidentTypeMap = new Map([
    ["ILSW", "Landslides"],
    ["IFLW", "Flood"],
    ["ITHS", "Heavy Rain & Thunderstorm"],
    ["HWN", "Severe Lightning"],
  ]);

  const iconEarlyWarningMap = new Map([
    ["LSW", "assets/al.png"],
    ["HRW", "assets/ah.png"],
    ["FLW", "assets/af.png"],
    ["TSM", "assets/tsunami_icon.png"],
    ["HWN", "assets/aw.png"],
    ["SAN", "assets/am.png"],
    ["LIG", "assets/alh.png"],
  ]);

  // const EWTypeMap = new Map([
  //   ["LSW","Landslides"],
  //   [ "FLW","Flood"],
  //   ["HRW","Heavy Rain & Thunderstorm"],
  //   ["LIG","Severe Lightning"],
  //   ["HWN","Heavy Wind"],
  // ]);

  const EWTypeMap = new Map([
    ["LSW", "Landslides"],
    ["FLW", "Flood"],
    ["HRW", "Rain"],
    ["LIG", "Lightning"],
    ["HWN", "Wind"],
  ]);

  const locationSuggestions: string[] = [
    "New York",
    "Los Angeles",
    "Chicago",
    "Houston",
    "Phoenix",
    "San Diego",
    "San Francisco",
    "Dallas",
    "Kurunegala",
  ];

  // Process warnings by data set
  const processWarningsByType = (warnings: earlyWarning[]) => {
    const summary: WarningSummary = {};

    warnings.forEach((warning) => {
      const { w_type, severity } = warning;
      // Initialize the severity count if not already present
      if (!summary[w_type]) {
        summary[w_type] = { Red: 0, Yellow: 0, Amber: 0 };
      }
      // Increment the count based on severity
      if (severity === "Red") {
        summary[w_type].Red += 1;
      } else if (severity === "Yellow") {
        summary[w_type].Yellow += 1;
      } else if (severity === "Amber") {
        summary[w_type].Amber += 1;
      }
    });

    setWarningSummary(summary);
  };

  // Process incidents by data set
  const processIncidentsByType = (incidents: hzIncidents[]) => {
    const tempSummary: IncidentSummary = {};
    incidents.forEach((incident) => {
      const { w_type } = incident;

      if (!tempSummary[w_type]) {
        tempSummary[w_type] = { count: 0 };
      }
      if (tempSummary[w_type]) {
        tempSummary[w_type].count += 1;
      }
    });

    setIncidentsSummary(tempSummary);
  };

  // Fetch data
  useEffect(() => {
    const gisArrayEarlyWarning: string[] = [
      "LK1",
      "LK2",
      "LK3",
      "LK4",
      "LK5",
      "LK6",
      "LK7",
      "LK8",
      "LK9",
      "SAR1",
      "SAR2",
      "SAR3",
      "SAR4",
      "SAR5",
      "SAR6",
      "SAR7",
      "SAR8",
      "SAR9",
    ];
    const queryStringEarlyWaring: string = `arealist=${gisArrayEarlyWarning.join(
      ","
    )}`;
    const earlyWaringURL: string = `https://mobile.mobilise-project.org.uk/mobisense/getEarlyWarningTest?${queryStringEarlyWaring}`;
    // const earlyWaringURL: string =  `https://mobile.mobilise-project.org.uk/mobisense/getEarlyWarningProd?${queryStringEarlyWaring}`;
    // Fetch incidents
    fetch(earlyWaringURL)
      .then((response) => {
        if (response.ok) {
          return response.json();
        } else {
          console.error("Invalid response data format:", response);
        }
      })
      .then((data) => {
        setEarlyWarnings(data);
        setEarlyWarningsFull(data);
        processWarningsByType(data);
        props.handleEarlyWarningData(data);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });

    const gisArrayIncidents = { arealist: ["LK1333", "LK1312"] };
    const queryStringIncident = Object.entries(gisArrayIncidents)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(
            JSON.stringify(value)
          )}`
      )
      .join("&");
    const incidentURL: string = `https://mobile.mobilise-project.org.uk/mobisense/getHzmByLocationTwoWeek?${queryStringIncident}`;
    // Fetch early warnings
    fetch(incidentURL)
      .then((response) => {
        if (response.ok) {
          return response.json();
        } else {
          console.error("Invalid response data format:", response);
        }
      })
      .then((data) => {
        setIncidents(data);
        processIncidentsByType(data);
        props.handleHzData(data);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  }, []);

  const clickEws = (ewsType: string) => {
    props.ewSeverityWindow(true);
    if (earlyWarnings) {
      const filteredWarnings = earlyWarnings.filter(
        (warning) => warning.severity === ewsType
      );
      props.onFilteredWarnings(filteredWarnings);
    } else {
      props.onFilteredWarnings([]);
    }
    if (layout === "modular") {
      props.openModular("modularOpened");
    }
    props.summeryType("severity");
  };

  const clickEwsType = (ewsType: string) => {
    props.typeWindow(true);
    if (earlyWarnings) {
      const filteredWarnings = earlyWarnings.filter(
        (warning) => warning.w_type === ewsType
      );
      props.onFilteredWarningsByType(filteredWarnings);
    } else {
      props.onFilteredWarningsByType([]);
    }
    props.summeryType("incidentType");
  };

  const clickHz = (hzType: string) => {
    props.typeWindow(true);
    if (incidents) {
      const filteredWarnings = incidents.filter(
        (warning) => warning.w_type === hzType
      );
      props.onFilteredHzByType(filteredWarnings);
    } else {
      props.onFilteredWarningsByType([]);
    }
    props.summeryType("incidentType");
  };

  const timeFromRef = useRef<HTMLInputElement | null>(null);
  const timeToRef = useRef<HTMLInputElement | null>(null);
  const [timePeriod, setTimePeriod] = useState<[string, string]>(["", ""]);
  const [isOpenTimePicker, setIsOpenTimePicker] = useState<boolean>(false);
  const [suggestionsT, setSuggestionsT] = useState<string[]>([]);

  const handleTimeChange = () => {
    if (timeFromRef && timeToRef) {
      if (timeFromRef.current?.value && timeToRef.current?.value) {
        setTimePeriod([timeFromRef.current.value, timeToRef.current.value]);
      }
    }
  };

  const handleTimeClear = () => {
    if (timeFromRef.current && timeToRef.current) {
      timeFromRef.current.value = "";
      timeToRef.current.value = "";
      setTimePeriod(["", ""]);
      setIsOpenTimePicker(false);
    }
  };
  // Input handle
  const handleInputSearch = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.value.length > 0) {
      const tempList: string[] = locationSuggestions.filter((str) =>
        str.toLowerCase().includes(event.target.value.toLowerCase())
      );
      setSuggestionsT(tempList);
    } else {
      setSuggestionsT([]);
    }
  };

  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<
    { name: string; lon: number; lat: number }[]
  >([]);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (query.length < 2) {
        setSuggestions([]);
        setShowDropdown(false);
        return;
      }
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${query}&format=json&addressdetails=1&limit=5`
        );
        const data = await response.json();

        const locations = data.map((location: any) => ({
          name: location.name,
          lon: parseFloat(location.lon),
          lat: parseFloat(location.lat),
        }));

        setSuggestions(locations);
        setShowDropdown(true);
      } catch (error) {
        console.error("Error fetching location suggestions:", error);
        setSuggestions([]);
        setShowDropdown(false);
      }
    };

    fetchSuggestions();
  }, [query]);

  const handleSearch = (lon: number, lat: number) => {
    updateMapCenter(parseFloat(lon.toString()), parseFloat(lat.toString()), 10);
    setShowDropdown(false);
    setQuery("");
  };

  const handleSuggestionClick = (lon: number, lat: number, name: string) => {
    setQuery(name); // Set input value to selected location name
    handleSearch(lon, lat);
  };

  return (
    <div className={`summaryBoxWrapper`}>
      {/* Time Wrapper */}
      <div className={`gridWrapper timeWrapper`}>
        <span className={`day`}>{new Date(currentTime).getDate()}</span>
        <span className={`date`}>
          {new Date(currentTime).toLocaleDateString("en-GB", {
            year: "numeric",
            month: "short",
          })}
        </span>
        <span className={`time`}>
          {new Date(currentTime).toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })}
          UTC
        </span>
      </div>

      {/* Incident count wrapper */}
      <div
        className={`gridWrapper incidentCountWrapper`}
        onClick={() => setClickedHzString("clickHz")}
        onTouchStart={() => setClickedHzString("clickHz")}
      >
        <img src={incidentIco} alt="" />

        {/* Render when incident 0 */}
        {incidents && incidents.length > 0 && (
          <div className={`countWrapper`}>
            <span className={`count`}>{incidents.length}</span>
            {/* Incident count */}
            <span className={`name`}>incidents</span>
            <span className={`range`}>(Last 2 weeks)</span>
          </div>
        )}

        {/* Render when incident grater than 0 */}
        {incidents && incidents.length === 0 && (
          <div className={`countWrapper`}>
            <span className={`name`} style={{ color: "rgb(75, 204, 71)" }}>
              No Any Incidents
            </span>
          </div>
        )}
      </div>

      {/* EW Count Wrapper */}
      <div
        className={`gridWrapper ewCountWrapper`}
        onClick={() => setClickedString("clickEws")}
        onTouchStart={() => setClickedString("clickEws")}
      >
        <img src={activeEWIco} alt="" />

        {/* Render when EW 0 */}
        {earlyWarnings && earlyWarnings.length === 0 && (
          <div className={`countWrapperNoData`}>
            <span className={`name`} style={{ color: "rgb(75, 204, 71)" }}>
              No Any Active EW
            </span>
          </div>
        )}

        {/* Render when EW grater than 0 */}
        {earlyWarnings && earlyWarnings.length > 0 && (
          <div className={`countWrapper`}>
            <span className={`count`}>{earlyWarnings.length}</span>
            {/* Early warning count */}
            <span className={`name`}>Active EW</span>
            <span style={{ visibility: "hidden", fontSize: "0.7rem" }}>
              hidden
            </span>
          </div>
        )}
      </div>

      {/* Render when both incidents and earlyWarnings are 0 */}
      {incidents &&
      incidents.length === 0 &&
      earlyWarnings &&
      earlyWarnings.length === 0 ? (
        <>
          {/* Incident header wrapper */}
          <div className={`gridWrapper incidentHeaderWrapper hideRow`}></div>
          {/* EW header wrapper */}
          <div className={`gridWrapper ewHeaderWrapper hideRow`}></div>
          {/* Incident detail wrapper */}
          <div className={`gridWrapper incidentDetailWrapper hideRow`}></div>
          {/* EW detail wrapper */}
          <div className={`gridWrapper ewDetailWrapper hideRow`}></div>
        </>
      ) : (
        <>
          {incidents && incidents.length > 0 ? (
            <>
              {/* Render when incident greater than */}
              {/* Incident header wrapper */}
              <div className={`gridWrapper incidentHeaderWrapper hideRow`}>
                <span className={`headerCount`}>Events</span>
              </div>

              {/* Incident detail wrapper */}
              <div className={`gridWrapper incidentDetailWrapper hideRow`}>
                {/* Incidents list */}
                {incidentsSummary &&
                  Object.keys(incidentsSummary).map((w_type) => (
                    <div
                    key={w_type}
                      className={`incidentItem`}
                      onClick={() => {
                        clickHz(w_type);
                        if (layout === "modular") {
                          props.openModular("modularOpened");
                        }
                      }}
                      onTouchStart={() => {
                        clickHz(w_type);
                        if (layout === "modular") {
                          props.openModular("modularOpened");
                        }
                      }}
                    >
                      <span className={`name`}>
                        <img src={iconHzMap.get(w_type)} alt="" />
                        {incidentTypeMap.get(w_type)}
                      </span>
                      {/* Set incident name icon here */}
                      <span className={`count`}>
                        {incidentsSummary[w_type].count}
                      </span>
                    </div>
                  ))}
              </div>
            </>
          ) : (
            <>
              {/* Render when incident 0 */}
              {/* Incident header wrapper */}
              <div
                className={`gridWrapper incidentHeaderWrapper hideRow`}
              ></div>

              {/* Incident detail wrapper */}
              <div className={`gridWrapper incidentDetailWrapper hideRow`}>
                <div className={`empty`}>                 
                  <img src={safeIco} alt="" />
                </div>
              </div>
            </>
          )}

          {earlyWarnings && earlyWarnings.length > 0 ? (
            <>
              {/* Render when incident greater than */}
              {/* EW header wrapper */}
              <div className={`gridWrapper ewHeaderWrapper hideRow`}>
                <span
                  className={`headerCount hide`}
                  style={{ visibility: "hidden" }}
                >
                  hidden
                </span>
                <span
                  className={`headerCount`}
                  style={{ color: "rgb(241, 65, 65)" }}
                  onClick={() => {
                    clickEws("Red");
                  }}
                  onTouchStart={() => {
                    clickEws("Red");
                  }}
                >
                  evacuation
                </span>
                {/* Red */}
                <span
                  className={`headerCount`}
                  style={{ color: "rgb(255, 191, 0)" }}
                  onClick={() => {
                    clickEws("Amber");
                  }}
                  onTouchStart={() => {
                    clickEws("Amber");
                  }}
                >
                  warning
                </span>
                {/* Amber */}
                <span
                  className={`headerCount`}
                  style={{ color: "rgb(255, 255, 0)" }}
                  onClick={() => {
                    clickEws("Yellow");
                  }}
                  onTouchStart={() => {
                    clickEws("Yellow");
                  }}
                >
                  watch
                </span>
                {/* Yellow */}
              </div>

              {/* EW detail wrapper */}
              <div className={`gridWrapper ewDetailWrapper hideRow`}>
                {/* Warning summary list */}
                {warningSummary &&
                  Object.keys(warningSummary).map((w_type) => (
                    <div key={w_type} className={`ewItem`}>
                      <span
                        className={`name`}
                        onClick={() => {
                          clickEwsType(w_type);
                          if (layout === "modular") {
                            props.openModular("modularOpened");
                          }
                        }}
                        onTouchStart={() => {
                          clickEwsType(w_type);
                          if (layout === "modular") {
                            props.openModular("modularOpened");
                          }
                        }}
                      >
                        <img src={iconEarlyWarningMap.get(w_type)} alt="" />
                        {EWTypeMap.get(w_type)}
                      </span>
                      {/* Set EW name icon here */}
                      <span className={`evacuation`}>
                        {warningSummary[w_type].Red}
                      </span>
                      <span className={`warning`}>
                        {warningSummary[w_type].Amber}
                      </span>
                      <span className={`watch`}>
                        {warningSummary[w_type].Yellow}
                      </span>
                    </div>
                  ))}
              </div>
            </>
          ) : (
            <>
              {/* Render when incident 0 */}
              {/* EW header wrapper */}
              <div className={`gridWrapper ewHeaderWrapper hideRow`}></div>

              {/* EW detail wrapper */}
              <div className={`gridWrapper ewDetailWrapper hideRow`}>
                <div className={`empty`}>
                  <img src={safeIco} alt="" />
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Time selecting wrapper */}
      {isOpenTimePicker ? (
        <div className={`gridWrapper time-selector hideRow`}>
          <div className={`input-container`}>
            <label htmlFor="summary-component-from">From</label>
            <input
              type="datetime-local"
              id="summary-component-from"
              ref={timeFromRef}
            />
          </div>
          <div className={`input-container`}>
            <label htmlFor="summary-component-to">To</label>
            <input
              type="datetime-local"
              id="summary-component-to"
              ref={timeToRef}
            />
          </div>

          {timePeriod[0] === "" || timePeriod[1] === "" ? (
            <div className={`input-btn-container`}>
              <button
                className={`set-button`}
                onClick={(e) => handleTimeChange()}
                onTouchStart={(e) => handleTimeChange()}
                style={{ backgroundColor: `var(--color-blue)` }}
              >
                set
              </button>
            </div>
          ) : (
            <div className={`input-btn-container`}>
              <button
                className={`set-button`}
                onClick={(e) => handleTimeClear()}
                onTouchStart={(e) => handleTimeClear()}
                style={{ backgroundColor: `var(--color-red)` }}
              >
                clear
              </button>
            </div>
          )}
          <img
            className={`close-btn`}
            src={closeIcon}
            alt=""
            onClick={(e) => {
              handleTimeClear();
              setIsOpenTimePicker(false);
            }}
            onTouchStart={(e) => {
              handleTimeClear();
              setIsOpenTimePicker(false);
            }}
          />
        </div>
      ) : (
        <div className={`gridWrapper time-selector-new hideRow`}>
          <span>in last 24 hours</span>
          <button onClick={(e) => setIsOpenTimePicker(true)}
                 onTouchStart={(e) => setIsOpenTimePicker(true)}
                >set time</button>
        </div>
      )}
      {/* Search wrapper */}
      <div className={`gridWrapper searchWrapper`}>
        <div className={`input-box-wrapper`}>
          <input
            type="text"
            placeholder="Search location"
            onFocus={() => setShowDropdown(true)}

            //
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              handleInputSearch(e);
            }}
          />
          {showDropdown && suggestions.length > 0 && (
            <div className={`suggestion-box`}>
              {suggestions.map((suggestion, index) => (
                <span
                  className={`suggestion`}
                  key={suggestion.name}
                  onClick={() =>
                    handleSuggestionClick(
                      suggestion.lon,
                      suggestion.lat,
                      suggestion.name
                    )
                  }
                  onTouchStart={() =>
                    handleSuggestionClick(
                      suggestion.lon,
                      suggestion.lat,
                      suggestion.name
                    )
                  }
                >
                  {suggestion.name}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* More info wrapper */}
      
      <div className={`gridWrapper infoWrapper`}>
        <span className={`lastUpdate`}>
          last updated: {new Date(lastUpdate).toTimeString()}
        </span>
        {earlyWarnings?.length !== 0 || incidents?.length !== 0 &&(
              <span
              className={`moreInfo`}
              onClick={(e) => {
                props.moreDetailWindow(true);
                if (layout === "modular") {
                  props.openModular("modularOpened");
                }
                props.summeryType("summeryFull");
              }}
              onTouchStart={(e) => {
                props.moreDetailWindow(true);
                if (layout === "modular") {
                  props.openModular("modularOpened");
                }
                props.summeryType("summeryFull");
              }}
            >
              <img src={infoIcon} alt="" /> more info
            </span>
        )}
    
      </div>

      {/* Close button */}
      <img
        className={`closeButton`}
        src={closeIcon}
        alt=""
        onClick={(e) => props.windowState(false)}
        onTouchStart={(e) => props.windowState(false)}
      />

      {/* Loading screen */}
      {!earlyWarnings && !incidents && (
        <div className={`loadingScreen`}>
          <img src={loadIco} alt="" /> {/* Loading image gif here */}
        </div>
      )}
    </div>
  );
};

export default SummaryComp;
